import { ArrowLeft, MoreHorizontal, Brain, Info } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { useNavigate } from "react-router-dom";

const Fitness2 = () => {
  const navigate = useNavigate();

  const score = 62;
  const baselineDelta = "+8";
  const confidence = 70;

  const signals = [
    {
      label: "HRV Deviation",
      value: "−12% vs baseline",
      status: "Elevated Load",
      statusColor: "bg-destructive/15 text-destructive",
      progress: 75,
      description:
        "Lower HRV relative to your baseline increases physiological load.",
      path: "/fitness2/hrv-deviation",
    },
    {
      label: "Sleep Recovery",
      value: "6.2 hrs",
      status: "Suboptimal",
      statusColor: "bg-warning/15 text-warning",
      progress: 55,
      description: "Sleep debt reduces recovery and increases stress load.",
      path: "/fitness2/sleep-recovery",
    },
    {
      label: "Resting Heart Rate Delta",
      value: "+5 bpm",
      status: "Elevated",
      statusColor: "bg-destructive/15 text-destructive",
      progress: 65,
      description: "Higher resting HR can indicate sympathetic activation.",
      path: "/fitness2/resting-hr",
    },
    {
      label: "Activity Recovery Buffer",
      value: "Low active recovery",
      status: "Neutral",
      statusColor: "bg-muted text-muted-foreground",
      progress: 35,
      description: "Light movement can modestly buffer physiological load.",
      path: "/fitness2/activity-recovery",
    },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Physiological Load
          </h1>
          <Button variant="ghost" size="icon" className="rounded-xl">
            <MoreHorizontal className="h-5 w-5" />
          </Button>
        </div>

        {/* Main Score Card */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-accent/40 to-background p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Brain className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-foreground">Physiological Load</h2>
              <p className="text-xs text-muted-foreground">
                Bite Balance Stress Index™
              </p>
            </div>
            <Badge
              variant="outline"
              className="rounded-full text-xs font-medium"
            >
              Modeled
            </Badge>
          </div>

          <div className="mb-3 flex items-baseline gap-2">
            <span className="text-5xl font-black text-foreground">{score}</span>
            <span className="text-lg text-muted-foreground">/ 100</span>
            <Badge className="ml-auto rounded-full border-0 bg-destructive/15 text-xs text-destructive">
              {baselineDelta} vs baseline
            </Badge>
          </div>

          <div className="mb-4 rounded-xl bg-muted/60 p-3">
            <p className="text-sm text-foreground">
              <strong>Moderate load</strong> detected. Elevated load can reduce
              glucose tolerance and lower your Bite Balance potential today.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="mb-4 grid grid-cols-3 gap-2">
            <div className="rounded-xl border border-border bg-background p-3 text-center">
              <p className="mb-1 text-[10px] text-muted-foreground">
                HRV vs baseline
              </p>
              <p className="text-sm font-bold text-foreground">−12%</p>
            </div>
            <div className="rounded-xl border border-border bg-background p-3 text-center">
              <p className="mb-1 text-[10px] text-muted-foreground">
                Sleep recovery
              </p>
              <p className="text-sm font-bold text-foreground">6.2 hrs</p>
            </div>
            <div className="rounded-xl border border-border bg-background p-3 text-center">
              <p className="mb-1 text-[10px] text-muted-foreground">
                Resting HR
              </p>
              <p className="text-sm font-bold text-foreground">+5 bpm</p>
            </div>
          </div>

          {/* Confidence Bar */}
          <div className="mb-4 flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Confidence</span>
            <Progress value={confidence} className="h-2 flex-1" />
            <span className="text-xs font-semibold text-foreground">
              {confidence}%
            </span>
          </div>

          {/* Action Buttons */}
          <div className="mb-3 grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="rounded-xl font-semibold"
              onClick={() => navigate("/fitness2/inputs")}
            >
              View Inputs
            </Button>
            <Button
              className="rounded-xl font-semibold"
              onClick={() => navigate("/fitness2/improve-accuracy")}
            >
              Improve Accuracy
            </Button>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            Uses available signals (sleep + HR trends). Connect Fitbit for
            higher accuracy.
          </p>
        </Card>

        {/* Stress Index Inputs */}
        <div className="space-y-1">
          <h2 className="px-1 text-lg font-bold text-foreground">
            Stress Index Inputs
          </h2>

          {/* Confidence & Circadian */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="rounded-xl border-0 bg-accent/30 p-4 shadow-sm">
              <p className="mb-1 text-xs font-semibold text-destructive">
                Confidence
              </p>
              <p className="text-lg font-bold text-foreground">{confidence}%</p>
              <p className="text-[10px] text-muted-foreground">
                Modeled (partial signals)
              </p>
            </Card>
            <Card className="rounded-xl border-0 bg-accent/30 p-4 shadow-sm">
              <p className="mb-1 text-xs font-semibold text-muted-foreground">
                Circadian Window
              </p>
              <p className="text-lg font-bold text-foreground">Evening</p>
              <p className="text-[10px] text-muted-foreground">
                Reduced sensitivity window
              </p>
            </Card>
          </div>
        </div>

        {/* Signal Breakdowns */}
        <div className="space-y-3">
          {signals.map((signal, i) => (
            <Card
              key={i}
              className="cursor-pointer rounded-xl border-0 p-4 shadow-sm transition-shadow hover:shadow-md active:scale-[0.99]"
              onClick={() => navigate(signal.path)}
            >
              <div className="mb-1 flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">
                  {signal.label}
                </p>
                <Badge
                  className={`${signal.statusColor} rounded-full border-0 text-[10px] font-semibold`}
                >
                  {signal.status}
                </Badge>
              </div>
              <p className="mb-2 text-xl font-bold text-foreground">
                {signal.value}
              </p>
              <Progress value={signal.progress} className="mb-2 h-2" />
              <p className="text-xs text-muted-foreground">
                {signal.description}
              </p>
            </Card>
          ))}
        </div>

        {/* Info Card */}
        <Card className="rounded-xl border-0 bg-muted/50 p-4 shadow-sm">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <p className="text-xs leading-relaxed text-muted-foreground">
              Your Stress Index is calculated from HRV deviation, sleep
              recovery, resting HR shift, and activity recovery. A circadian
              adjustment is applied (morning tends to be more metabolically
              tolerant than evening). Confidence reflects how complete and
              recent your signals are.
            </p>
          </div>
        </Card>

        {/* Unavailable State Card */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
              <Brain className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-foreground">Without Fitbit</h3>
              <p className="text-xs text-muted-foreground">
                Bite Balance Stress Index™
              </p>
            </div>
            <Badge variant="outline" className="rounded-full text-xs">
              Unavailable
            </Badge>
          </div>
          <div className="mb-3 flex items-baseline gap-2">
            <span className="text-3xl font-black text-muted-foreground/30">
              —
            </span>
            <span className="text-sm text-muted-foreground">/ 100</span>
            <Badge variant="outline" className="ml-auto rounded-full text-xs">
              Sync needed
            </Badge>
          </div>
          <div className="mb-4 rounded-xl bg-muted/50 p-3">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">No signals yet.</strong>{" "}
              Connect Fitbit (or enable permissions) to generate your stress
              index.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="rounded-xl font-semibold"
              onClick={() => navigate("/fitness2/improve-accuracy")}
            >
              Learn More
            </Button>
            <Button
              className="rounded-xl font-semibold"
              onClick={() => navigate("/fitbit-connect")}
            >
              Connect Fitbit
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Fitness2;
